import { useState, useRef, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Col, Row } from "react-bootstrap";
import API from "../axios";
import ColumnToggleDialog from "../components/PurchaseOrderPivotView/ColumnToggleDialog";
import OrderStatusView from "../components/PurchaseOrderPivotView/OrderStatusView";
import TimelineView from "../components/PurchaseOrderPivotView/TimelineView";
import TimelineViewWeekly from "../components/PurchaseOrderPivotView/TimelineViewWeekly";
import TimelineViewMonthly from "../components/PurchaseOrderPivotView/TimelineViewMontly";
import DashboardStatus from "../components/Dashboard/DashboardStatus";
import InfoTable from "../components/PurchaseOrderPivotView/InfoTable";

const VehicleShippingStatus = () => {
    const [shippingData, setShippingData] = useState([]);
    const [dashboardData, setDashboardData] = useState();
    const [delayedList, setDelayedList] = useState([]);
    const [dateColumns, setDateColumns] = useState([]);

    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const suggestions = [
        "PO Number",
        "Vendor Ref",
        "BL Number",
        "Product Name",
    ];

    /**
     * For daily view
     */
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    // For weekly and montly view
    const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());

    const [currentStartDate, setCurrentStartDate] = useState("");
    const [currentEndDate, setCurrentEndDate] = useState("");

    const [loading, setLoading] = useState(true);
    const [shipment, setShipment] = useState("current");
    const [tableView, setTableView] = useState<
        "timeline" | "stage" | "dashboard"
    >();

    // Add a new state for view type
    const [viewType, setViewType] = useState<"daily" | "weekly" | "monthly">();

    const [search, setSearch] = useState("");
    const [poNumber, setPoNumber] = useState("");
    const [blNumber, setBlNumber] = useState("");
    const [vendorReference, setVendorReference] = useState("");
    const [productName, setProductName] = useState("");

    /**
     * To sync two tables left and right
     */
    const leftScrollRef = useRef<HTMLDivElement | null>(null);
    const rightScrollRef = useRef<HTMLDivElement | null>(null);
    const isSyncing = useRef(false);

    const [shippingInfoColumns, setShippingInfoColumns] = useState([
        {
            name: "PO NUMBER",
            visiable: true,
        },
        {
            name: "VENDOR REF",
            visiable: true,
        },
        {
            name: "BL NUMBER",
            visiable: true,
        },
        {
            name: "Product",
            visiable: true,
        },
        {
            name: "DROPSHIP",
            visiable: true,
        },
        {
            name: "Qty Ordered",
            visiable: true,
        },
        {
            name: "Waiting QTY",
            visiable: true,
        },
        {
            name: "Qty Shipped",
            visiable: true,
        },
        {
            name: "Next Step",
            visiable: true,
        },
    ]);

    function getMonthDates(
        targetMonth: number,
        targetYear: number,
    ): { startDate: string; endDate: string } {
        const startDate = new Date(targetYear, targetMonth - 1, 1);
        const endDate = new Date(targetYear, targetMonth, 0);

        const formatDate = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        };

        return {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
        };
    }

    async function handleSearch(item?: string) {
        setIsSearchOpen(false);
        setLoading(true);
        try {
            let filter: any = {};

            if (item) {
                if (item == "PO Number") filter.poNumber = search.trim();
                else if (item == "Vendor Ref")
                    filter.vendorReference = search.trim();
                else if (item == "BL Number") filter.blNumber = search.trim();
                else if (item == "Product Name")
                    filter.productName = search.trim();

                let result: any;
                result = await API.getShippingList(
                    undefined,
                    undefined,
                    filter,
                );

                setDateColumns(result.dates);
                setShippingData(result.data);
            } else {
                if (poNumber) filter.poNumber = poNumber.trim();
                if (blNumber) filter.blNumber = blNumber.trim();
                if (vendorReference)
                    filter.vendorReference = vendorReference.trim();
                if (productName) filter.productName = productName.trim();

                let result: any;
                if (poNumber || blNumber || vendorReference || productName) {
                    result = await API.getShippingList(
                        undefined,
                        undefined,
                        filter,
                    );
                } else {
                    // Use appropriate date range based on view type
                    let dates;
                    if (viewType === "weekly" || viewType == "monthly") {
                        dates = {
                            startDate: `${monthlyYear}-01-01`,
                            endDate: `${monthlyYear}-12-31`,
                        };
                    } else {
                        dates = getMonthDates(currentMonth, currentYear);
                    }

                    /**
                     * Sync the dates to component state
                     */
                    setCurrentStartDate(dates.startDate);
                    setCurrentEndDate(dates.endDate);

                    result = await API.getShippingList(
                        dates.startDate,
                        dates.endDate,
                    );
                }

                setDateColumns(result.dates);
                setShippingData(result.data);
                setDashboardData(result.dashboardData);
                setDelayedList(result.delayedList);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    }

    async function handleClearSearch() {
        setPoNumber("");
        setVendorReference("");
        setBlNumber("");
        setProductName("");

        if (viewType === "monthly" || viewType == "weekly") {
            loadShippingDataMonthly();
        } else {
            loadShippingData();
        }
    }

    async function loadShippingData(
        month = currentMonth,
        year = currentYear,
        params: any = {},
    ) {
        setLoading(true);
        params.shippment = shipment;

        const dates = getMonthDates(month, year);

        setCurrentStartDate(dates.startDate);
        setCurrentEndDate(dates.endDate);

        try {
            const result: any = await API.getShippingList(
                dates.startDate,
                dates.endDate,
                params,
            );
            setDateColumns(result.dates);
            setShippingData(result.data);
            setDashboardData(result.dashboardData);
            setDelayedList(result.delayedList);
        } catch (error) {
            console.error("Error loading shipping data:", error);
        } finally {
            setLoading(false);
        }
    }

    // Monthly view navigation
    const handlePreviousYear = () => {
        const newYear = monthlyYear - 1;
        setMonthlyYear(newYear);
        loadShippingDataMonthly(newYear);
    };

    const handleNextYear = () => {
        const newYear = monthlyYear + 1;
        setMonthlyYear(newYear);
        loadShippingDataMonthly(newYear);
    };

    const handleViewTypeChange = (
        newViewType: "daily" | "weekly" | "monthly",
    ) => {
        setViewType(newViewType);

        if (newViewType === "monthly" || viewType == "weekly") {
            // Set current year for monthly view
            const currentYear = new Date().getFullYear();
            setMonthlyYear(currentYear);
            loadShippingDataMonthly(currentYear);
        } else {
            loadShippingData();
        }
    };

    // Monthly data loading function
    async function loadShippingDataMonthly(year?: number, params: any = {}) {
        setLoading(true);
        params.shippment = shipment;

        const useYear = year ?? monthlyYear;

        console.log("Loading monthly data for year:", useYear);

        try {
            // For monthly view, use the entire year
            const startDate = `${useYear}-01-01`;
            const endDate = `${useYear}-12-31`;

            setCurrentStartDate(startDate);
            setCurrentEndDate(endDate);

            const result: any = await API.getShippingList(
                startDate,
                endDate,
                params,
            );

            setDateColumns(result.dates);
            setShippingData(result.data);
            setDashboardData(result.dashboardData.data);
            setDelayedList(result.delayedList);
        } catch (error) {
            console.error("Error loading monthly shipping data:", error);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    }

    const handlePreviousMonth = () => {
        let newMonth = currentMonth - 1;
        let newYear = currentYear;

        if (newMonth < 1) {
            newMonth = 12;
            newYear -= 1;
        }

        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
        loadShippingData(newMonth, newYear);
    };

    const handleNextMonth = () => {
        let newMonth = currentMonth + 1;
        let newYear = currentYear;

        if (newMonth > 12) {
            newMonth = 1;
            newYear += 1;
        }

        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
        loadShippingData(newMonth, newYear);
    };

    const getMonthYearDisplay = () => {
        const monthNames = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];
        return `${monthNames[currentMonth - 1]} ${currentYear}`;
    };

    const handleScroll = (source: any, target: any) => {
        if (isSyncing.current) return;

        isSyncing.current = true;

        if (source.current && target.current) {
            target.current.scrollTop = source.current.scrollTop;
        }

        requestAnimationFrame(() => {
            isSyncing.current = false;
        });
    };

    const saveColumnStates = function () {
        localStorage.setItem(
            "columnState",
            JSON.stringify(shippingInfoColumns),
        );
    };

    const saveViewState = function () {
        if (tableView) {
            localStorage.setItem("view", tableView);
        }
    };

    const saveViewType = function () {
        if (viewType) {
            localStorage.setItem("viewType", viewType);
        }
    };

    const loadPreviousColumnStates = function () {
        const cleared = localStorage.getItem("cleared3");
        if (!cleared) {
            localStorage.removeItem("columnState");
            localStorage.setItem("cleared3", "true");
        }

        const previousState = localStorage.getItem("columnState");
        if (previousState) {
            setShippingInfoColumns(JSON.parse(previousState));
        }
    };

    const loadViewState = function () {
        const previousState = localStorage.getItem("view");
        if (previousState) {
            setTableView(previousState as "timeline" | "stage");
        } else {
            setTableView("timeline");
        }
    };

    const loadViewType = function () {
        const previousState = localStorage.getItem("viewType");
        if (previousState) {
            setViewType(previousState as "daily" | "weekly" | "monthly");
        } else {
            setViewType("daily");
        }
    };

    useEffect(() => {
        loadPreviousColumnStates();
        loadViewType();
        loadViewState();

        // Load daily data by default
        loadShippingData();
    }, []);

    useEffect(() => {
        if (!search) {
            handleClearSearch();
        }
    }, [search]);

    useEffect(() => {
        if (viewType === "monthly" || viewType == "weekly") {
            loadShippingDataMonthly();
        }

        saveViewType();
    }, [viewType]);

    useEffect(() => {
        saveViewState();
    }, [tableView]);

    return (
        <div className="min-vh-100" style={{ overflow: "hidden" }}>
            <div className="container-fluid" style={{ marginTop: 100 }}>
                {/* Header */}
                <Row>
                    <Col cols="4" className="d-flex align-items-center gap-2">
                        <a
                            className="btn btn-outline-black btn-md mx-1"
                            href={`/`}
                        >
                            <i className="bi bi-chevron-left fs-4"></i>
                        </a>
                        <h3 className="fw-medium text-primary">
                            Shipping Tracking
                        </h3>
                    </Col>
                    <Col
                        cols="8"
                        className=" d-flex align-items-center justify-content-end gap-2"
                    >
                        <div className="col-md-6 p-0">
                            <div className="position-relative">
                                <div className="input-group">
                                    <input
                                        style={{
                                            maxHeight: 30,
                                        }}
                                        type="text"
                                        className="form-control"
                                        placeholder="Search..."
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                        onFocus={() => setIsSearchOpen(true)}
                                        onBlur={() =>
                                            setTimeout(() => {
                                                setIsSearchOpen(false);
                                            }, 1000)
                                        }
                                    />
                                    <button
                                        className="btn btn-primary"
                                        type="button"
                                        style={{
                                            maxHeight: 30,
                                            fontSize: 10,
                                        }}
                                    >
                                        <i className="bi bi-search"></i>
                                    </button>
                                </div>

                                {search && isSearchOpen && (
                                    <div
                                        className="dropdown-menu show w-100 position-absolute"
                                        style={{ top: "100%", zIndex: 1000 }}
                                    >
                                        {suggestions.map((item, index) => (
                                            <button
                                                key={index}
                                                className="dropdown-item"
                                                style={{
                                                    fontSize: 15,
                                                }}
                                                onClick={() =>
                                                    handleSearch(item)
                                                }
                                            >
                                                <b>{item}</b> : {search}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <select
                            className="form-select form-select-sm mx-1"
                            style={{ width: "auto", display: "inline-block" }}
                            value={viewType}
                            onChange={(e) =>
                                handleViewTypeChange(
                                    e.target.value as
                                        | "daily"
                                        | "weekly"
                                        | "monthly",
                                )
                            }
                        >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                        <button
                            className={`btn btn-sm ${
                                tableView == "stage"
                                    ? "btn-secondary text-white"
                                    : "btn-outline-secondary"
                            }`}
                            onClick={() => setTableView("stage")}
                        >
                            <i className="bi bi-check-circle"></i>
                        </button>
                        <button
                            className={`btn btn-sm ${
                                tableView == "timeline"
                                    ? "btn-secondary text-white"
                                    : "btn-outline-secondary"
                            }`}
                            onClick={() => setTableView("timeline")}
                        >
                            <i className="bi bi-calendar2-range"></i>
                        </button>
                        <button
                            className={`btn btn-sm ${
                                tableView == "dashboard"
                                    ? "btn-secondary text-white"
                                    : "btn-outline-secondary"
                            }`}
                            onClick={() => setTableView("dashboard")}
                        >
                            <i className="bi bi-speedometer"></i>
                        </button>
                        <div
                            style={{
                                height: "50%",
                                width: 1,
                                backgroundColor: "#cacacaff",
                            }}
                        ></div>
                        <button
                            className="btn btn-sm btn-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#columnToggle"
                        >
                            <i className="bi bi-columns"></i>
                        </button>
                        <button
                            className="btn btn-sm btn-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#filterModal"
                        >
                            <i className="bi bi-funnel"></i>
                        </button>
                        <a href="/shipping-fullscreen">
                            <button className="btn btn-sm btn-outline">
                                <i className="bi bi-fullscreen"></i>
                            </button>
                        </a>

                        {viewType === "daily" ? (
                            <>
                                <button
                                    onClick={handlePreviousMonth}
                                    className="btn btn-flat"
                                    style={{ borderRadius: "8px" }}
                                    disabled={loading}
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <span
                                    style={{
                                        fontSize: "1rem",
                                        fontWeight: "600",
                                        color: "#495057",
                                        minWidth: "150px",
                                        textAlign: "center",
                                    }}
                                >
                                    {getMonthYearDisplay()}
                                </span>
                                <button
                                    onClick={handleNextMonth}
                                    className="btn btn-flat"
                                    style={{
                                        borderRadius: "8px",
                                        padding: "0.5rem 1rem",
                                    }}
                                    disabled={loading}
                                >
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </>
                        ) : viewType === "weekly" ? (
                            <>
                                <button
                                    onClick={handlePreviousYear}
                                    className="btn btn-flat"
                                    style={{ borderRadius: "8px" }}
                                    disabled={loading}
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <span
                                    style={{
                                        fontSize: "1rem",
                                        fontWeight: "600",
                                        color: "#495057",
                                        minWidth: "150px",
                                        textAlign: "center",
                                    }}
                                >
                                    {monthlyYear}
                                </span>
                                <button
                                    onClick={handleNextYear}
                                    className="btn btn-flat"
                                    style={{
                                        borderRadius: "8px",
                                        padding: "0.5rem 1rem",
                                    }}
                                    disabled={loading}
                                >
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </>
                        ) : (
                            // Monthly view navigation
                            <>
                                <button
                                    onClick={handlePreviousYear}
                                    className="btn btn-flat"
                                    style={{ borderRadius: "8px" }}
                                    disabled={loading}
                                    title="Previous Year"
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <span
                                    style={{
                                        fontSize: "1.2rem",
                                        fontWeight: "700",
                                        color: "#495057",
                                        minWidth: "120px",
                                        textAlign: "center",
                                    }}
                                >
                                    {monthlyYear}
                                </span>
                                <button
                                    onClick={handleNextYear}
                                    className="btn btn-flat"
                                    style={{
                                        borderRadius: "8px",
                                        padding: "0.5rem 1rem",
                                    }}
                                    disabled={loading}
                                    title="Next Year"
                                >
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </>
                        )}
                    </Col>
                </Row>

                <div
                    className="card"
                    style={{
                        marginTop: "1rem",
                        marginBottom: 100,
                        borderWidth: 1,
                        borderColor: "#d8d8d8ff",
                    }}
                >
                    {loading ? (
                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ minHeight: "70vh" }}
                        >
                            <div className="text-center">
                                <div
                                    className="spinner-border text-primary"
                                    role="status"
                                    style={{
                                        width: "3rem",
                                        height: "3rem",
                                    }}
                                >
                                    <span className="visually-hidden">
                                        Loading...
                                    </span>
                                </div>
                                <p className="mt-3 text-muted">
                                    Loading shipping data...
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                style={{
                                    position: "relative",
                                    overflowY: "hidden",
                                    overflowX: "hidden",
                                }}
                            >
                                {tableView == "dashboard" ? (
                                    <DashboardStatus
                                        startDate={currentStartDate}
                                        endDate={currentEndDate}
                                        dashboardData={dashboardData}
                                        delayedList={delayedList}
                                    />
                                ) : (
                                    <>
                                        {shippingData.length > 0 ? (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    maxHeight: "70vh",
                                                }}
                                            >
                                                {/* Scrollable columns - reduced width */}
                                                <div
                                                    className="scrollbar-hide"
                                                    style={{
                                                        display: "flex",
                                                        flexShrink: 0,
                                                        backgroundColor:
                                                            "white",
                                                        maxWidth: "40%",
                                                        overflowY: "scroll",
                                                    }}
                                                    ref={leftScrollRef}
                                                    onScroll={() =>
                                                        handleScroll(
                                                            leftScrollRef,
                                                            rightScrollRef,
                                                        )
                                                    }
                                                >
                                                    <InfoTable
                                                        shippment={shipment}
                                                        shippingInfoColumns={
                                                            shippingInfoColumns
                                                        }
                                                        shippingData={
                                                            shippingData
                                                        }
                                                    />
                                                </div>
                                                <div
                                                    className="scrollbar-hide"
                                                    style={{
                                                        display: "flex",
                                                        flex: 1,
                                                        flexShrink: 0,
                                                        backgroundColor:
                                                            "white",
                                                        width: "60%",
                                                        overflowY: "scroll",
                                                        borderLeft:
                                                            "1px solid #efefef",
                                                        paddingBottom: 35,
                                                    }}
                                                    ref={rightScrollRef}
                                                    onScroll={() =>
                                                        handleScroll(
                                                            rightScrollRef,
                                                            leftScrollRef,
                                                        )
                                                    }
                                                >
                                                    {tableView == "stage" && (
                                                        <OrderStatusView
                                                            shippingData={
                                                                shippingData
                                                            }
                                                        />
                                                    )}
                                                    {tableView ==
                                                        "timeline" && (
                                                        <>
                                                            {viewType ==
                                                                "daily" && (
                                                                <TimelineView
                                                                    shippingData={
                                                                        shippingData
                                                                    }
                                                                    dateColumns={
                                                                        dateColumns
                                                                    }
                                                                    fullDate={
                                                                        false
                                                                    }
                                                                />
                                                            )}
                                                            {viewType ==
                                                                "weekly" && (
                                                                <TimelineViewWeekly
                                                                    shippingData={
                                                                        shippingData
                                                                    }
                                                                    currentYear={
                                                                        monthlyYear
                                                                    }
                                                                />
                                                            )}
                                                            {viewType ==
                                                                "monthly" && (
                                                                <TimelineViewMonthly
                                                                    shippingData={
                                                                        shippingData
                                                                    }
                                                                    currentYear={
                                                                        monthlyYear
                                                                    }
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                style={{
                                                    height: "70vh",
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    fontSize: 16,
                                                    color: "#525252ff",
                                                }}
                                            >
                                                No Data Found
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Filter Modal */}
            <div
                className="modal fade"
                id="filterModal"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Filter Shipping Data
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            />
                        </div>
                        <div className="modal-body">
                            <Col className="mt-3">
                                <label className="form-label">Shipments</label>
                                <select
                                    className="form-select"
                                    aria-label="Default select example"
                                    value={shipment}
                                    onChange={(e) =>
                                        setShipment(e.target.value)
                                    }
                                >
                                    <option value="current">
                                        Current Shipments
                                    </option>
                                    <option value="all">All Shipments</option>
                                </select>
                            </Col>
                            <Col className="mt-3">
                                <label className="form-label">PO Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={poNumber}
                                    onChange={(e) =>
                                        setPoNumber(e.target.value)
                                    }
                                />
                            </Col>
                            <Col className="mt-3">
                                <label className="form-label">Vendor Ref</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={vendorReference}
                                    onChange={(e) =>
                                        setVendorReference(e.target.value)
                                    }
                                />
                            </Col>
                            <Col className="mt-3">
                                <label className="form-label">BL Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={blNumber}
                                    onChange={(e) =>
                                        setBlNumber(e.target.value)
                                    }
                                />
                            </Col>
                            <Col className="mt-3">
                                <label className="form-label">
                                    Produt Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={productName}
                                    onChange={(e) =>
                                        setProductName(e.target.value)
                                    }
                                />
                            </Col>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={handleClearSearch}
                            >
                                Clear Search
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={() => handleSearch()}
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <ColumnToggleDialog
                columns={shippingInfoColumns}
                setColumns={setShippingInfoColumns}
                saveColumnState={saveColumnStates}
            />

            <style>{`

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .table-hover tbody tr:hover {
          background-color: #f8f9fa;
        }
        
        div[style*="overflowX: scroll"]::-webkit-scrollbar {
          height: 8px;
        }
        
        div[style*="overflowX: scroll"]::-webkit-scrollbar-track {
          background: #f7fafc;
          border-radius: 4px;
        }
        
        div[style*="overflowX: scroll"]::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 4px;
        }
        
        div[style*="overflowX: scroll"]::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
      `}</style>
        </div>
    );
};

export default VehicleShippingStatus;
